// Переписка по задаче: сообщения агентов, людей и системы + форма комментария.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { api, uploadFile } from '../api.ts'
import { useBusy } from '../lib/hooks.ts'
import { MESSAGE_KIND_LABELS, MESSAGE_KIND_TONES, formatDateTime, timeAgo } from '../lib/format.ts'
import { Markdown } from '../lib/markdown.tsx'
import { TaskLink } from '../router.tsx'
import { useAgentMap, useNow } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import type { AttachmentInfo, Message } from '../types.ts'
import { AttachmentGrid, FilePicker, MAX_FILE_BYTES, PendingFiles, useFileDrop } from './Attachments.tsx'
import { ActorLabel, AgentLabel } from './AgentAvatar.tsx'
import { AgentSelect } from './AgentSelect.tsx'
import { DirectionChip } from './chips.tsx'
import { Icon } from './Icon.tsx'
import { Badge, Button, Collapsible, Empty, Kbd, cx } from './ui.tsx'

function ToLabel({ m }: { m: Message }) {
  switch (m.to_type) {
    case 'agent':
      return <AgentLabel id={m.to_id} size="xs" link />
    case 'direction':
      return m.to_id ? <DirectionChip id={m.to_id} /> : <span>направлению</span>
    case 'human':
      return <span className="font-medium text-warn">тебе</span>
    case 'broadcast':
      return <span>всем</span>
    default:
      return null
  }
}

/** Переход к сообщению в ленте без смены адреса страницы. */
function JumpLink({ to, className, children }: { to: number; className?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        const el = document.getElementById(`msg-${to}`)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.animate?.([{ boxShadow: '0 0 0 3px var(--accent)' }, { boxShadow: '0 0 0 0 transparent' }], { duration: 1400 })
      }}
    >
      {children}
    </button>
  )
}

/** Сообщение коллеги из обсуждения связанной задачи Битрикс24 — OMA перенесла его в переписку сама. */
function BitrixMessage({ m, showTask }: { m: Message; showTask?: boolean }) {
  const now = useNow()
  const author = String((m.data as { author?: string } | null)?.author ?? m.subject.replace(/^Битрикс24 · /, ''))
  return (
    <article id={`msg-${m.id}`} className="relative rounded-card border border-line bg-surface px-3.5 py-3 shadow-card">
      <header className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
        <span className="inline-flex min-w-0 items-center gap-1.5 font-medium">
          <Icon name="message" size={14} className="shrink-0 text-muted" />
          <span className="truncate">{author}</span>
        </span>
        <Badge tone="accent">Битрикс24</Badge>
        {showTask && m.task_id ? (
          <TaskLink id={m.task_id} className="font-mono text-[11px] text-muted hover:underline">
            OMA-{m.task_id}
          </TaskLink>
        ) : null}
        <time className="tnum ml-auto shrink-0 text-[11.5px] text-subtle" dateTime={m.created_at} title={formatDateTime(m.created_at)}>
          {timeAgo(m.created_at, now)}
        </time>
      </header>
      <p className="mt-1.5 whitespace-pre-wrap text-[13.5px] text-fg-2 [overflow-wrap:anywhere]">{m.body}</p>
      {msgFiles(m).length ? (
        <div className="mt-2">
          <AttachmentGrid items={msgFiles(m)} taskId={m.task_id ?? 0} />
        </div>
      ) : null}
    </article>
  )
}

export function MessageItem({ m, showTask }: { m: Message; showTask?: boolean }) {
  const now = useNow()
  if (m.from_type === 'system' && m.from_id === 'bitrix24') return <BitrixMessage m={m} showTask={showTask} />
  if (m.from_type === 'system') {
    return (
      <div className="flex items-start gap-2 px-1 py-1 text-[12px] text-muted" id={`msg-${m.id}`}>
        <Icon name="dot" size={14} className="mt-0.5 shrink-0 text-subtle" />
        <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
          {m.subject ? <span className="font-medium text-fg-2">{m.subject}. </span> : null}
          <span className="whitespace-pre-wrap">{m.body}</span>
        </div>
        {showTask && m.task_id ? (
          <TaskLink id={m.task_id} className="shrink-0 font-mono text-[11px] hover:underline">
            OMA-{m.task_id}
          </TaskLink>
        ) : null}
        <time className="tnum shrink-0 text-[11px] text-subtle" dateTime={m.created_at} title={formatDateTime(m.created_at)}>
          {timeAgo(m.created_at, now)}
        </time>
      </div>
    )
  }
  const human = m.from_type === 'human'
  const waiting = m.requires_reply && !m.answered_by
  const dataKeys = Object.keys(m.data ?? {}).filter((k) => k !== 'attachments' && k !== 'escalation_id')
  return (
    <article
      id={`msg-${m.id}`}
      className={cx(
        'relative rounded-card border px-3.5 py-3 shadow-card',
        human ? 'border-line bg-info-soft' : 'border-line bg-surface',
      )}
    >
      {m.kind === 'escalation' || (m.requires_reply && !m.answered_by) ? (
        <span aria-hidden="true" className="absolute inset-y-3 left-[-1px] w-[3px] rounded-r bg-warn" />
      ) : null}
      <header className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
        <span className="min-w-0 max-w-full font-medium">
          <ActorLabel type={m.from_type} id={m.from_id} size="sm" link />
        </span>
        {m.to_type !== 'task' ? (
          <span className="inline-flex min-w-0 max-w-full items-center gap-1 text-muted">
            <Icon name="arrowRight" size={12} className="shrink-0" />
            <ToLabel m={m} />
          </span>
        ) : null}
        <Badge tone={MESSAGE_KIND_TONES[m.kind] ?? 'neutral'}>{MESSAGE_KIND_LABELS[m.kind] ?? m.kind}</Badge>
        {waiting ? (
          <Badge tone="warn" title="Отправитель ждёт ответа">
            ждёт ответа
          </Badge>
        ) : m.requires_reply && m.answered_by ? (
          <JumpLink to={m.answered_by} className="inline-flex items-center gap-0.5 text-[11.5px] text-ok hover:underline">
            <Icon name="check" size={12} />
            отвечено
          </JumpLink>
        ) : null}
        {m.reply_to ? (
          <JumpLink to={m.reply_to} className="text-[11.5px] text-subtle hover:underline">
            в ответ на #{m.reply_to}
          </JumpLink>
        ) : null}
        {showTask && m.task_id ? (
          <TaskLink id={m.task_id} className="font-mono text-[11px] text-muted hover:underline">
            OMA-{m.task_id}
          </TaskLink>
        ) : null}
        <time
          className="tnum ml-auto shrink-0 text-[11.5px] text-subtle"
          dateTime={m.created_at}
          title={formatDateTime(m.created_at)}
        >
          {timeAgo(m.created_at, now)}
        </time>
      </header>
      {m.subject ? <div className="mt-2 text-[13.5px] font-semibold [overflow-wrap:anywhere]">{m.subject}</div> : null}
      <Markdown text={m.body} className="mt-1 text-[13.5px] text-fg-2" />
      {msgFiles(m).length ? (
        <div className="mt-2">
          <AttachmentGrid items={msgFiles(m)} taskId={m.task_id ?? 0} />
        </div>
      ) : null}
      {dataKeys.length ? (
        <Collapsible summary={`данные (${dataKeys.join(', ')})`} className="mt-1.5">
          <pre className="scroll-thin max-h-60 overflow-auto rounded-ctl border border-line bg-surface-2 p-2 font-mono text-[11.5px] max-md:whitespace-pre-wrap max-md:[overflow-wrap:anywhere]">
            {JSON.stringify(Object.fromEntries(Object.entries(m.data ?? {}).filter(([k]) => dataKeys.includes(k))), null, 2)}
          </pre>
        </Collapsible>
      ) : null}
    </article>
  )
}

export function MessageThread({ messages, emptyText = 'Сообщений пока нет', showTask }: { messages: Message[]; emptyText?: string; showTask?: boolean }) {
  if (!messages.length) return <Empty icon="message">{emptyText}</Empty>
  return (
    <div className="flex flex-col gap-2">
      {messages.map((m) => (
        <MessageItem key={m.id} m={m} showTask={showTask} />
      ))}
    </div>
  )
}

/** Файлы, приложенные к сообщению (владелец — к комментарию или ответу на вопрос). */
const msgFiles = (m: Message): AttachmentInfo[] => {
  const a = (m.data as { attachments?: AttachmentInfo[] } | null)?.attachments
  return Array.isArray(a) ? a : []
}

export function Composer({
  taskId,
  assigneeId,
  onSent,
}: {
  taskId: number
  assigneeId: string | null
  onSent?: (m: Message) => void
}) {
  const agents = useAgentMap()
  const [body, setBody] = useState('')
  const [to, setTo] = useState('')
  const [busy, run] = useBusy()
  const ref = useRef<HTMLTextAreaElement>(null)
  const id = useId()
  // файлы к сообщению: перетаскивание и вставка скриншота, пока открыта переписка, идут сюда, а не в задачу
  const [files, setFiles] = useState<File[]>([])
  const [progress, setProgress] = useState<(number | undefined)[]>([])
  const addFiles = (fs: File[]) => {
    const big = fs.filter((f) => f.size > MAX_FILE_BYTES)
    if (big.length) toast(`Больше 100 МБ, не прикреплено: ${big.map((f) => f.name).join(', ')}`, 'error')
    const ok = fs.filter((f) => f.size <= MAX_FILE_BYTES && f.size > 0)
    if (ok.length) setFiles((cur) => [...cur, ...ok])
  }
  useFileDrop(addFiles)

  useEffect(() => {
    setTo('')
    setFiles([])
  }, [taskId])

  const send = () =>
    run(async () => {
      const text = body.trim()
      if (!text && !files.length) return
      try {
        const ids: number[] = []
        for (let i = 0; i < files.length; i++) {
          setProgress((p) => Object.assign([...p], { [i]: 0 }))
          const a = await uploadFile<AttachmentInfo>(files[i]!, (x) => setProgress((p) => Object.assign([...p], { [i]: x })))
          ids.push(a.id)
          setProgress((p) => Object.assign([...p], { [i]: 1 }))
        }
        const payload: Record<string, unknown> = { body: text }
        if (to) payload.to_agent_id = to
        if (ids.length) payload.attachments = ids
        const m = await api.post<Message>(`/api/tasks/${taskId}/comment`, payload)
        setBody('')
        setFiles([])
        setProgress([])
        onSent?.(m)
        ref.current?.focus()
      } catch (e) {
        setProgress([])
        toastError(e, 'Сообщение не отправлено')
      }
    })

  const assignee = assigneeId ? agents.get(assigneeId) : undefined
  return (
    <form
      className="flex flex-col gap-2 rounded-card border border-line-strong bg-surface p-2.5 shadow-card focus-within:border-accent/50"
      onSubmit={(e) => {
        e.preventDefault()
        void send()
      }}
    >
      <label htmlFor={id} className="sr-only">
        Сообщение агентам
      </label>
      <textarea
        id={id}
        ref={ref}
        className="ctl min-h-[72px] border-transparent bg-transparent px-1 hover:border-transparent focus:border-transparent focus:shadow-none"
        placeholder="Комментарий, уточнение или указание агенту… Скриншот — ⌘V, файлы — перетащите сюда"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            void send()
          }
        }}
      />
      <PendingFiles files={files} progress={progress} onRemove={(i) => setFiles((cur) => cur.filter((_, k) => k !== i))} />
      <div className="flex flex-wrap items-center gap-2">
        <FilePicker onFiles={addFiles} size="xs" label="Файлы" />
        <span className="text-[12px] text-muted">Кому:</span>
        <AgentSelect
          value={to}
          onChange={setTo}
          ariaLabel="Получатель"
          emptyLabel={assignee ? `Исполнитель — ${assignee.name}` : assigneeId ? `Исполнитель — ${assigneeId}` : 'Исполнитель / штаб'}
          className="h-7 w-auto min-w-0 max-w-[min(280px,100%)] py-0.5 text-[12.5px]"
        />
        <span className="ml-auto hidden text-[11.5px] text-subtle sm:inline">
          <Kbd>⌘</Kbd> <Kbd>Enter</Kbd>
        </span>
        <Button type="submit" variant="primary" size="sm" icon="send" loading={busy} disabled={!body.trim() && !files.length}>
          Отправить
        </Button>
      </div>
    </form>
  )
}
